import { t } from './textos.js';

/**
 * Regras do Player 1 que o tablet decide sozinho (Figma, quadros 29–33;
 * docs/player1-integracao.md). A fase é individual e offline-first: o
 * catálogo chega com os efeitos de cada alternativa, o tablet aplica os
 * deltas e resolve o perfil para exibir — o servidor reapura tudo no sync.
 *
 * RN-P1-02 / RN-P1-03: os indicadores não são pontuação competitiva e não
 * alimentam ranking; nada aqui produz colocação.
 */

/** Ordem em que os indicadores aparecem na tela (Figma, quadro 4). */
export const INDICADORES = Object.freeze([
    { chave: 'fidelizacao', get rotulo () { return t('p1.indicador_fidelizacao'); } },
    { chave: 'confianca', get rotulo () { return t('p1.indicador_confianca'); } },
    { chave: 'agilidade', get rotulo () { return t('p1.indicador_agilidade'); } },
    { chave: 'conexao', get rotulo () { return t('p1.indicador_conexao'); } }
]);

/** Cada indicador começa em 0: a pontuação é só o que as decisões somam. */
export const VALOR_INICIAL = 0;

/** Barra de 10 segmentos; cheia em 125 (Figma, quadro 15: 125 → 10, 110 → 8, 30 → 2). */
export const SEGMENTOS = 10;
export const VALOR_DA_BARRA_CHEIA = 125;

const paragrafos = (texto) => String(texto ?? '').split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean);

/**
 * O enunciado traz, em parágrafos: o título do capítulo, a situação e, por
 * último, a pergunta exibida acima das alternativas (nota do catálogo).
 */
export function partirEnunciado (enunciado) {
    const partes = paragrafos(enunciado);
    if (partes.length < 3) return { titulo: '', situacao: partes.slice(0, -1), pergunta: partes.at(-1) ?? '' };
    // Título de capítulo não leva ponto final na tela.
    return { titulo: partes[0].replace(/\.$/, ''), situacao: partes.slice(1, -1), pergunta: partes.at(-1) };
}

/** Converte o catálogo do backend (`GET /player1/catalogo`) no formato das cenas. */
export function paraJornada (catalogo) {
    const atividade = [...(catalogo?.atividades ?? [])].sort((a, b) => a.ordem - b.ordem)[0] ?? null;
    return {
        titulo: atividade?.titulo ?? 'PLAYER 1',
        abertura: atividade?.modal_inicio ?? '',
        encerramento: atividade?.modal_fim ?? '',
        capitulos: [...(atividade?.capitulos ?? [])].sort((a, b) => a.ordem - b.ordem).map((capitulo) => ({
            id: capitulo.id,
            ordem: capitulo.ordem,
            ...partirEnunciado(capitulo.enunciado),
            tempoSegundos: capitulo.tempo_segundos ?? null,
            opcoes: capitulo.opcoes.map((opcao) => ({
                id: opcao.id, letra: opcao.letra, texto: opcao.texto,
                efeitos: { ...(opcao.efeitos ?? {}) }, justificativa: opcao.justificativa ?? ''
            }))
        })),
        faixas: [...(catalogo?.faixas ?? [])].sort((a, b) => a.ordem - b.ordem)
    };
}

/**
 * Texto de abertura em páginas (RI-P1-02, "toque para passar"): o primeiro
 * parágrafo é o título e os demais se dividem em páginas de até
 * `porPagina`, as maiores primeiro (7 parágrafos → 3, 2, 2).
 */
export function paginarAbertura (texto, porPagina = 3) {
    const [titulo = '', ...resto] = paragrafos(texto);
    if (resto.length === 0) return { titulo, paginas: [] };
    const total = Math.ceil(resto.length / porPagina);
    const base = Math.floor(resto.length / total);
    let sobra = resto.length % total;
    const paginas = [];
    for (let i = 0, inicio = 0; i < total; i++) {
        const tamanho = base + (sobra-- > 0 ? 1 : 0);
        paginas.push(resto.slice(inicio, inicio + tamanho));
        inicio += tamanho;
    }
    return { titulo, paginas };
}

/**
 * "…dimensões da experiência: CONFIANÇA | CONEXÃO | …" → o texto até os
 * dois-pontos e a indicação de que ali entra a legenda com os ícones.
 */
export function separarLegenda (paragrafo) {
    const achado = /^(.*:)\s*([^:|]+(?:\|[^:|]+)+)$/s.exec(paragrafo);
    return achado ? { texto: achado[1].trim(), legenda: true } : { texto: paragrafo, legenda: false };
}

export function indicadoresIniciais () {
    return Object.fromEntries(INDICADORES.map(({ chave }) => [chave, VALOR_INICIAL]));
}

/** Sem piso nem teto: um indicador pode ficar negativo (não fazer clamp, §6.1). */
export function aplicarEfeitos (indicadores, efeitos = {}) {
    const novo = { ...indicadores };
    for (const [chave, delta] of Object.entries(efeitos)) {
        if (chave in novo) novo[chave] += delta;
    }
    return novo;
}

/**
 * Indicadores depois das decisões tomadas, aplicadas **na ordem do
 * catálogo** — é a ordem que o servidor usa para a curva do relatório.
 */
export function indicadoresDaJornada (capitulos, decisoes) {
    return capitulos.reduce((indicadores, capitulo) => {
        const decisao = decisoes.find((d) => d.capitulo_id === capitulo.id);
        const opcao = decisao && capitulo.opcoes.find((o) => o.id === decisao.opcao_id);
        return opcao ? aplicarEfeitos(indicadores, opcao.efeitos) : indicadores;
    }, indicadoresIniciais());
}

/** Indicador intocado continua valendo 50 e entra na soma. */
export function somar (indicadores) {
    return INDICADORES.reduce((soma, { chave }) => soma + (indicadores[chave] ?? VALOR_INICIAL), 0);
}

/** Faixa de perfil: limites inclusivos, vindos do catálogo; `null` é ponta aberta. */
export function resolverFaixa (faixas, soma) {
    return faixas.find((faixa) => (faixa.minimo ?? -Infinity) <= soma && soma <= (faixa.maximo ?? Infinity)) ?? null;
}

/** Quantos dos 10 segmentos da barra acendem para um valor. */
export function segmentos (valor) {
    return Math.max(0, Math.min(SEGMENTOS, Math.floor((valor * SEGMENTOS) / VALOR_DA_BARRA_CHEIA)));
}

/** Deltas de uma alternativa na ordem dos indicadores: [{ chave, texto: "+10" }]. */
export function impactos (efeitos = {}) {
    return INDICADORES
        .filter(({ chave }) => Number.isFinite(efeitos[chave]) && efeitos[chave] !== 0)
        .map(({ chave }) => ({ chave, delta: efeitos[chave], texto: `${efeitos[chave] > 0 ? '+' : '-'}${Math.abs(efeitos[chave])}` }));
}

/**
 * Texto da faixa: o primeiro parágrafo descreve o perfil (tela de
 * resultado) e o restante é a mensagem do pop-up de finalização (Figma,
 * quadros 15 e 16). Quando essa mensagem abre com uma saudação curta
 * ("Parabéns!!!!"), ela vira o título do pop-up.
 */
export function partirPerfil (texto) {
    const [descricao = '', ...mensagem] = paragrafos(texto);
    const saudacao = (mensagem[0] ?? '').length <= 30 ? (mensagem.shift() ?? '') : '';
    return { descricao, saudacao, mensagem };
}

/** "1:30" — o cronômetro do capítulo. */
export function formatarTempo (segundos) {
    const total = Math.max(0, Math.ceil(segundos));
    return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
}

const CHAVE = (participanteId) => `fiat-arcade:player1:v1:${participanteId}`;

const novoEstado = () => ({
    inicio: null,
    fim: null,
    aberturaVista: false,
    decisoes: [],            // { capitulo_id, opcao_id, timestamp_tablet } — definitivas (RN-P1-05)
    prazos: {},              // { [capituloId]: ISO } — fim do tempo de decisão
    perfilExibido: null,
    chaveEnvio: null,
    enviado: false,
    concluido: false         // chegou à tela de perfil
});

/** RN-P1-06: a sequência de decisões fica salva no tablet até a conclusão do módulo. */
export class ProgressoPlayer1 {
    constructor (participanteId, { armazenamento = globalThis.localStorage, agora = () => new Date() } = {}) {
        this._chave = CHAVE(participanteId);
        this._armazenamento = armazenamento;
        this._agora = agora;
        this.estado = novoEstado();
        try {
            const bruto = armazenamento?.getItem(this._chave);
            if (bruto) this.estado = { ...novoEstado(), ...JSON.parse(bruto) };
        } catch { /* estado novo */ }
    }

    iniciar () {
        if (!this.estado.inicio) {
            this.estado.inicio = this._agora().toISOString();
            this._gravar();
        }
    }

    marcarAberturaVista () {
        this.estado.aberturaVista = true;
        this._gravar();
    }

    /** Prazo do capítulo: fixado na primeira vez, para recarregar o app não devolver tempo. */
    prazoDe (capitulo) {
        if (!capitulo.tempoSegundos) return null;
        if (!this.estado.prazos[capitulo.id]) {
            this.estado.prazos[capitulo.id] = new Date(this._agora().getTime() + capitulo.tempoSegundos * 1000).toISOString();
            this._gravar();
        }
        return new Date(this.estado.prazos[capitulo.id]);
    }

    /** O facilitador pausou o grupo: o tempo parado não é descontado de nenhum prazo em curso. */
    adiarPrazos (milissegundos) {
        if (!(milissegundos > 0)) return;
        for (const [id, prazo] of Object.entries(this.estado.prazos)) {
            this.estado.prazos[id] = new Date(new Date(prazo).getTime() + milissegundos).toISOString();
        }
        this._gravar();
    }

    segundosRestantes (capitulo) {
        const prazo = this.prazoDe(capitulo);
        return prazo ? (prazo.getTime() - this._agora().getTime()) / 1000 : Infinity;
    }

    decisaoDe (capitulo) {
        return this.estado.decisoes.find((d) => d.capitulo_id === capitulo.id) ?? null;
    }

    /** Índice do primeiro capítulo ainda sem decisão (ou o total, se acabou). */
    proximoIndice (capitulos) {
        const indice = capitulos.findIndex((capitulo) => !this.decisaoDe(capitulo));
        return indice === -1 ? capitulos.length : indice;
    }

    /**
     * RN-P1-05 / RI-P1-06: decisão tomada é definitiva — uma segunda para o
     * mesmo capítulo é ignorada. Devolve se a decisão foi registrada.
     */
    decidir (capitulo, opcao) {
        if (this.decisaoDe(capitulo)) return false;
        if (!capitulo.opcoes.some((o) => o.id === opcao.id)) return false;
        this.estado.decisoes.push({
            capitulo_id: capitulo.id, opcao_id: opcao.id, timestamp_tablet: this._agora().toISOString()
        });
        this._gravar();
        return true;
    }

    /** Fim da jornada: guarda o perfil mostrado, que o servidor compara com o apurado (§6.2). */
    fechar (perfilExibido) {
        if (!this.estado.fim) this.estado.fim = this._agora().toISOString();
        this.estado.perfilExibido = perfilExibido ?? null;
        this.estado.concluido = true;
        this._gravar();
    }

    /**
     * Corpo de `POST /player1/resultado` e a chave de idempotência, sempre a
     * mesma entre tentativas. Indicadores e soma não viajam: o servidor
     * reapura. Não há post-it por capítulo no Figma, então a lista vai vazia.
     */
    envio (gerarChave = () => globalThis.crypto.randomUUID()) {
        if (!this.estado.chaveEnvio) {
            this.estado.chaveEnvio = gerarChave();
            this._gravar();
        }
        const { inicio, fim, perfilExibido, decisoes } = this.estado;
        return {
            chave: this.estado.chaveEnvio,
            corpo: {
                inicio_tablet: inicio, fim_tablet: fim,
                ...(perfilExibido ? { perfil_exibido: perfilExibido } : {}),
                decisoes, post_its: []
            }
        };
    }

    marcarEnviado () {
        this.estado.enviado = true;
        this._gravar();
    }

    reiniciar () {
        this.estado = novoEstado();
        this._gravar();
    }

    _gravar () {
        try { this._armazenamento?.setItem(this._chave, JSON.stringify(this.estado)); } catch { /* sem storage */ }
    }
}
